import { forwardRef, type ComponentPropsWithoutRef, type ReactNode } from 'react';
import { cn } from './cn.js';

export type Tone = 'neutral' | 'brand' | 'accent' | 'ok' | 'warn' | 'danger' | 'info';

/**
 * Tinted background + matching text + a hairline ring. The ring is what stops
 * a pale tint from dissolving into the surface, and it is why these read
 * cleanly in both themes without separate dark-mode values.
 */
const TONE: Record<Tone, string> = {
  neutral: 'bg-surface-sunken text-ink-secondary ring-line',
  brand: 'bg-brand-tint text-brand ring-brand-line/60',
  accent: 'bg-accent-tint text-accent ring-accent-line/60',
  ok: 'bg-ok-tint text-ok ring-ok-line/60',
  warn: 'bg-warn-tint text-warn ring-warn-line/60',
  danger: 'bg-danger-tint text-danger ring-danger-line/60',
  info: 'bg-info-tint text-info ring-info-line/60',
};

export interface BadgeProps extends ComponentPropsWithoutRef<'span'> {
  tone?: Tone;
  /** A leading status dot. Never the only carrier of meaning — the label is. */
  dot?: boolean;
  icon?: ReactNode;
  size?: 'sm' | 'md';
  children: ReactNode;
}

/**
 * FORWARDS ITS REF, because this is routinely a tooltip trigger.
 *
 * Radix passes the trigger a ref through `Slot`. A plain function component
 * cannot take one: React warns, the ref is dropped, and the tooltip anchors
 * against something other than the badge. The rest of the props are spread
 * for the same reason — a trigger's ARIA attributes have to reach the
 * element, not be thrown away on the way.
 */
export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(function Badge(
  { tone = 'neutral', dot = false, icon, size = 'sm', className, children, ...rest },
  ref,
) {
  return (
    <span
      ref={ref}
      {...rest}
      className={cn(
        'inline-flex max-w-full items-center gap-1 whitespace-nowrap rounded-sm font-medium ring-1 ring-inset',
        size === 'sm' ? 'px-1.5 py-px text-2xs' : 'px-2 py-0.5 text-xs',
        TONE[tone],
        className,
      )}
    >
      {dot && (
        <span
          aria-hidden
          className="h-1.5 w-1.5 shrink-0 rounded-full bg-current opacity-80"
        />
      )}
      {icon && (
        <span aria-hidden className="shrink-0 [&>svg]:h-3 [&>svg]:w-3">
          {icon}
        </span>
      )}
      <span className="truncate">{children}</span>
    </span>
  );
});

/** A bare status dot for use inside dense rows, always beside a text label. */
export function Dot({ tone = 'neutral', className }: { tone?: Tone; className?: string }) {
  const color: Record<Tone, string> = {
    neutral: 'bg-ink-tertiary',
    brand: 'bg-brand',
    accent: 'bg-accent',
    ok: 'bg-ok',
    warn: 'bg-warn',
    danger: 'bg-danger',
    info: 'bg-info',
  };
  return (
    <span
      aria-hidden
      className={cn('inline-block h-1.5 w-1.5 shrink-0 rounded-full', color[tone], className)}
    />
  );
}
